import React from 'react';
import { Link } from 'react-router-dom';
import { CategoryBadge } from './CategoryBadge';
import { fileUrl } from '@/lib/api';
import { colorForCategory } from '@/lib/categories';
import { ArrowUpRight } from 'lucide-react';

const ACCENTS = ['#0FFFE2', '#DF1283', '#D0FF0D', '#FC861E'];

export default function RecipientCard({ recipient, index = 0 }) {
    const primary = recipient.awards?.[0];
    const accent = primary ? colorForCategory(primary) : ACCENTS[index % ACCENTS.length];
    const initials = `${recipient.firstName?.[0] || ''}${recipient.lastName?.[0] || ''}`;

    return (
        <Link
            to={`/recipients/${recipient.slug}`}
            data-testid={`recipient-card-${recipient.slug}`}
            className="card-lift group relative flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-neutral-950 hover:border-white/30 focus:outline-none focus:ring-2 focus:ring-un-berry"
            style={{ animation: `fade-up 0.6s ${index * 60}ms cubic-bezier(.22,1,.36,1) both` }}
        >
            {/* Photo (circular) */}
            <div className="relative px-6 pt-8 pb-4 flex items-center justify-center bg-neutral-950">
                {/* soft radial glow behind circle */}
                <div
                    aria-hidden
                    className="absolute inset-x-6 top-8 bottom-4 rounded-full opacity-25 blur-2xl"
                    style={{ background: accent }}
                />
                <div className="relative">
                    {/* ring accent */}
                    <div
                        className="absolute inset-0 rounded-full"
                        style={{ boxShadow: `0 0 0 4px ${accent}` }}
                        aria-hidden
                    />
                    {recipient.photoUrl ? (
                        <img
                            src={fileUrl(recipient.photoUrl)}
                            alt={`${recipient.firstName} ${recipient.lastName}`}
                            loading="lazy"
                            className="relative h-44 w-44 md:h-52 md:w-52 rounded-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                        />
                    ) : (
                        <div
                            className="relative h-44 w-44 md:h-52 md:w-52 rounded-full flex items-center justify-center font-display text-5xl font-black text-black"
                            style={{ background: accent }}
                        >
                            {initials || 'UN'}
                        </div>
                    )}
                </div>

                {/* milestones */}
                <div className="absolute top-3 right-3 rounded-full border border-white/20 bg-black/70 backdrop-blur-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                    {recipient.totalMilestones ?? 0} milestones
                </div>

                {/* year awarded (optional) */}
                {recipient.awardYear ? (
                    <div
                        data-testid={`recipient-year-${recipient.slug}`}
                        className="absolute top-3 left-3 rounded-full border border-white/20 bg-black/70 backdrop-blur-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-un-lime tabular-nums"
                    >
                        {recipient.awardYear}
                    </div>
                ) : null}
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col gap-3 p-5 md:p-6">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h3 className="font-display text-xl md:text-2xl font-bold leading-tight text-white">
                            {recipient.firstName} {recipient.lastName}
                        </h3>
                        {recipient.companyName && (
                            <p className="mt-1 text-sm text-white/60 font-body">
                                {recipient.companyName}
                            </p>
                        )}
                    </div>
                    <span
                        className="mt-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/20 text-white transition-colors group-hover:border-un-lime group-hover:text-un-lime"
                    >
                        <ArrowUpRight size={16} />
                    </span>
                </div>

                <div className="mt-1 flex flex-wrap gap-1.5">
                    {(recipient.awards || []).map((a) => (
                        <CategoryBadge key={a} name={a} size="sm" />
                    ))}
                </div>
            </div>
        </Link>
    );
}
